<template>
  <section class="app-transporte">
    <v-container grid-list-xl style="max-width: 600px">
      <v-layout row wrap>
        <v-flex lg12 sm12 xs12>
          <v-card outlined class="app-card login-card">
            <v-card-title class="justify-center app-card-title">
              Cadastro Positivo de Veículos
            </v-card-title>

            <v-card-text class="app-card-content">
              <v-form ref="form" v-model="formValid" lazy-validation>
                <v-row>
                  <v-col cols="12">
                    <v-text-field
                      v-model="form.cpf"
                      label="CPF do Presidente *"
                      placeholder="000.000.000-00"
                      v-mask="'###.###.###-##'"
                      :rules="[rules.required, rules.cpf]"
                      filled
                      rounded
                      dense
                      @input="loginError = ''"
                    />
                  </v-col>
                </v-row>

                <v-row>
                  <v-col cols="12">
                    <v-text-field
                      v-model="form.token"
                      label="Token de Acesso *"
                      :rules="[rules.required]"
                      filled
                      rounded
                      dense
                      @input="loginError = ''"
                    />
                  </v-col>
                </v-row>

                <v-row class="justify-center mt-4 mb-2">
                  <v-col cols="12" class="d-flex justify-center">
                    <div class="recaptcha-container">
                      <div class="recaptcha-box">
                        <vue-recaptcha
                          ref="recaptcha"
                          :sitekey="siteKey"
                          :loadRecaptchaScript="true"
                          @verify="verifyRecaptcha"
                          @expired="expiredRecaptcha"
                        />
                      </div>
                    </div>
                  </v-col>
                </v-row>

                <v-row v-if="loginError">
                  <v-col cols="12">
                    <v-alert
                      type="error"
                      dense
                      outlined
                      class="mb-0"
                    >
                      {{ loginError }}
                    </v-alert>
                  </v-col>
                </v-row>
              </v-form>
            </v-card-text>

            <v-card-actions class="justify-end actions-row">
              <v-spacer />

              <v-btn
                depressed
                large
                class="btn-clear"
                style="color: #1976d2; background-color: #d5e9ef"
                @click="limpar"
              >
                Limpar
              </v-btn>

              <v-btn
                depressed
                large
                color="primary"
                :loading="loading"
                :disabled="!canSubmit"
                @click="entrar"
              >
                Entrar
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-flex>
      </v-layout>
    </v-container>
  </section>
</template>

<script>
import { mask } from 'vue-the-mask';
import VueRecaptcha from 'vue-recaptcha';
import { VUE_APP_RECAPTCHA_KEY } from '@/commons/config';
import { CpvAuthService } from '@/services/cpv/cpv.auth.service';
import { validateCpf } from '@/helpers/cpf.validator';

const cpvAuthService = new CpvAuthService();

export default {
  name: 'CPVLogin',

  components: {
    VueRecaptcha,
  },

  directives: {
    mask,
  },

  data: () => ({
    siteKey: VUE_APP_RECAPTCHA_KEY,
    loading: false,
    loginError: '',
    formValid: false,
    form: {
      cpf: '',
      token: '',
      recaptcha: null,
    },
    rules: {
      required: v => (!!v && v !== undefined) || 'Esse campo deve ser preenchido',
      cpf: value => {
        const digits = String(value || '').replace(/\D/g, '');
        return (digits.length === 11 && !!validateCpf(digits)) || 'CPF inválido';
      },
    },
  }),

  computed: {
    cpfDigits() {
      return String(this.form.cpf || '').replace(/\D/g, '');
    },

    cpfValido() {
      return this.cpfDigits.length === 11 && !!validateCpf(this.cpfDigits);
    },

    tokenPreenchido() {
      return !!String(this.form.token || '').trim();
    },

    canSubmit() {
      return (
        !this.loading &&
        !!this.form.recaptcha &&
        this.tokenPreenchido &&
        this.cpfValido &&
        this.formValid === true
      );
    },
  },

  methods: {
    verifyRecaptcha(value) {
      this.form.recaptcha = value;
      this.loginError = '';
    },

    expiredRecaptcha() {
      this.form.recaptcha = null;
      this.$refs.recaptcha?.reset();
    },

    limpar() {
      this.$refs.form?.reset();
      this.$refs.recaptcha?.reset();
      this.form.recaptcha = null;
      this.loginError = '';
      this.formValid = false;
    },

    entrar() {
      this.loginError = '';

      if (!this.$refs.form.validate()) return;

      if (!this.form.recaptcha) {
        this.loginError = 'Confirme que você não é um robô para continuar.';
        return;
      }

      this.loading = true;

      cpvAuthService.login(
        this.cpfDigits,
        this.form.token
      ).subscribe({
        next: res => {
          localStorage.setItem('cpv_token', res.token);

          const redirect =
            this.$route.query.redirect ||
            '/cpv/dashboard';

          this.$router.replace(redirect);
        },
        error: err => {
          this.loginError = this.getFriendlyLoginError(err);
          this.form.recaptcha = null;
          this.$refs.recaptcha?.reset();
          this.loading = false;
        },
        complete: () => {
          this.loading = false;
        },
      });
    },

    getFriendlyLoginError(err) {
      const backendMessage = this.extractBackendMessage(err).toLowerCase();
      const status = err?.response?.status || err?.status;

      if (!err?.response && !err?.error && !err?.message) {
        return 'Não foi possível conectar ao servidor. Tente novamente.';
      }

      if (backendMessage.includes('presidente inativo')) {
        return 'Seu acesso está inativo. Procure o administrador do sistema.';
      }

      if (
        backendMessage.includes('token inválido') ||
        backendMessage.includes('token invalido') ||
        backendMessage.includes('cpf inválido') ||
        backendMessage.includes('cpf invalido') ||
        backendMessage.includes('credenciais inválidas') ||
        backendMessage.includes('credenciais invalidas') ||
        status === 401 ||
        status === 403
      ) {
        return 'CPF ou token de acesso inválidos.';
      }

      if (status === 404) {
        return 'Não foi possível realizar o login com os dados informados.';
      }

      if (status >= 500) {
        return 'Serviço temporariamente indisponível. Tente novamente em instantes.';
      }

      return 'Não foi possível realizar o login. Revise os dados e tente novamente.';
    },

    extractBackendMessage(err) {
      return (
        err?.response?.data?.detail ||
        err?.response?.data?.message ||
        err?.error?.detail ||
        err?.error?.message ||
        err?.message ||
        ''
      );
    },
  },
};
</script>

<style scoped>
.login-card {
  width: 420px;
  overflow: visible;
}

.recaptcha-container {
  width: 100%;
  display: flex;
  justify-content: center;
}

.recaptcha-box {
  width: 100%;
  max-width: 340px;
  min-height: 90px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.actions-row {
  padding: 16px;
  gap: 12px;
}
</style>